import { Box, Button, Center, CloseButton, Spinner } from "@chakra-ui/react";
import type { DocusealForm } from "@docuseal/react";
import type React from "react";
import type { ComponentProps } from "react";
import { useCallback, useEffect, useState } from "react";

import { DocusealBaseForm } from "./DocusealBaseForm";

type DocusealOverlayProps = ComponentProps<typeof DocusealForm> & {
  isOpen: boolean;
  onClose?: () => void;
  /** Optional pre-built signature image for the countersigner flow */
  signature?: string;
  /** Optionally show a Dismiss button in the footer */
  hasDismiss?: boolean;
};

/**
 * A full-screen overlay that renders the DocuSeal signing form.
 *
 * - Fades in when `isOpen` becomes true, fades out when closed.
 * - Matches the `ModalOverlay` backdrop blur.
 * - Clicking the backdrop (outside the form) will close the overlay.
 */
export const DocusealOverlay: React.FC<DocusealOverlayProps> = ({
  isOpen,
  onClose = () => {},
  signature,
  hasDismiss = false,
  ...formProps
}) => {
  // Track whether the overlay is mounted in the DOM (for exit animation)
  const [mounted, setMounted] = useState(false);

  // Track the visible (opacity) state separately so we can animate out
  const [visible, setVisible] = useState(false);

  // Track whether the DocuSeal form has finished loading
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoaded(false);
      setMounted(true);
      // Allow a frame for the element to mount before triggering the transition
      requestAnimationFrame(() => {
        setVisible(true);
      });
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // After the fade-out transition ends, unmount
  const handleTransitionEnd = useCallback(() => {
    if (!visible) {
      setMounted(false);
    }
  }, [visible]);

  // Close when clicking the backdrop (not the form itself)
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target !== e.currentTarget) return;
      onClose();
    },
    [onClose],
  );

  if (!mounted) return null;

  return (
    <Box
      position="fixed"
      inset="0"
      zIndex="overlay"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="blackAlpha.600"
      backdropFilter="blur(8px)"
      opacity={visible ? 1 : 0}
      transition="opacity 0.25s ease-in-out"
      onTransitionEnd={handleTransitionEnd}
      onClick={handleBackdropClick}
      overflowY="auto">
      <Box
        bg="white"
        borderRadius={{ base: "md", md: "xl" }}
        position={hasDismiss ? "relative" : "static"}
        w={{ base: "calc(100vw - 8px)", md: "800px", lg: "960px" }}
        maxW="960px"
        minH={{ base: "calc(100dvh - 20px)", md: "calc(100vh - 10px)" }}
        maxH={{ base: "calc(100dvh - 20px)", md: "calc(100vh - 10px)" }}
        overflowY="auto"
        p={{ base: 4, md: 6 }}
        onClick={(e) => e.stopPropagation()}
        boxShadow="xl">
        <CloseButton
          position="fixed"
          top={3}
          right={3}
          size={{ base: "md", md: "lg" }}
          onClick={onClose}
          aria-label="Close signing modal"
          zIndex="overlay"
          bg="white"
          borderRadius="full"
          boxShadow="0 1px 3px rgba(0,0,0,0.08)"
        />

        {!loaded && (
          <Center position="absolute" inset={0} zIndex="0">
            <Spinner size="xl" color="gray.500" thickness="3px" />
          </Center>
        )}

        <Box
          {...(hasDismiss
            ? {
                overflow: "auto",
                maxH: { base: "calc(100dvh - 60px)", md: "calc(100vh - 210px)" },
                height: { base: "calc(100dvh - 60px)", md: "calc(100vh - 210px)" },
                mb: "4",
              }
            : {})}
          opacity={loaded ? 1 : 0}
          transition="opacity 0.2s ease-in">
          <DocusealBaseForm
            {...formProps}
            {...(signature
              ? { signature: signature.startsWith("data:") ? signature : `data:image/png;base64,${signature}` }
              : {})}
            customCss={`
              @media (max-width: 768px) {
                #submit_form_button,
                #start_form_button,
                .expand-form-button,
                .start-form-submit-button {
                  margin-bottom: 50px;
                }
              }
              ${formProps.customCss ?? ""}
            `}
            onLoad={() => setLoaded(true)}
          />
        </Box>

        {hasDismiss && (
          <Button onClick={onClose} variant="outline" w="full">
            Dismiss
          </Button>
        )}
      </Box>
    </Box>
  );
};
